import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Save } from "lucide-react";
import { getToken, authFetch } from "@/adminAuth";

const API = `/api`;

const input =
  "w-full bg-transparent border border-white/15 focus:border-gold outline-none px-4 py-3 text-sm text-[#FAF8F5] transition-colors";

export default function AdminTeam() {
  const navigate = useNavigate();
  const [team, setTeam] = useState(null);
  const [savedSlug, setSavedSlug] = useState("");

  useEffect(() => {
    if (!getToken()) navigate("/admin/login");
  }, [navigate]);

  useEffect(() => {
    fetch(`${API}/team`).then((r) => r.json()).then(setTeam).catch(() => {});
  }, []);

  if (!team) return <div className="pt-40 min-h-screen px-6 text-[#626773] font-mono text-sm">Chargement…</div>;

  const update = (slug, key, value) =>
    setTeam(team.map((m) => (m.slug === slug ? { ...m, [key]: value } : m)));

  const save = async (m) => {
    const resp = await authFetch(`${API}/admin/team/${m.slug}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: m.name, role: m.role, barreau: m.barreau, languages: m.languages, bio: m.bio, bio_en: m.bio_en, bio_nl: m.bio_nl, image: m.image, email: m.email, phone: m.phone }),
    });
    if (resp.ok) {
      setSavedSlug(m.slug);
      setTimeout(() => setSavedSlug(""), 2500);
    }
  };

  return (
    <div className="pt-32 pb-24 min-h-screen" data-testid="admin-team-page">
      <div className="max-w-4xl mx-auto px-6 sm:px-12">
        <Link
          to="/admin"
          data-testid="admin-team-back"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#A0A5B1] hover:text-gold transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Les fiches
        </Link>

        <h1 className="mt-8 font-serif font-light text-3xl sm:text-4xl">L'équipe</h1>
        <p className="mt-3 text-sm text-[#626773]">
          Biographies et rôles affichés sur les pages profils publiques.
        </p>

        <div className="mt-10 space-y-10">
          {team.map((m) => (
            <div key={m.slug} className="bg-ink-card border border-white/5 p-8" data-testid={`admin-member-${m.slug}`}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="label-accent block mb-3">Nom</label>
                  <input value={m.name} onChange={(e) => update(m.slug, "name", e.target.value)} className={input} data-testid={`admin-member-name-${m.slug}`} />
                </div>
                <div>
                  <label className="label-accent block mb-3">Rôle</label>
                  <input value={m.role} onChange={(e) => update(m.slug, "role", e.target.value)} className={input} data-testid={`admin-member-role-${m.slug}`} />
                </div>
                <div>
                  <label className="label-accent block mb-3">Barreau / fonction</label>
                  <input value={m.barreau} onChange={(e) => update(m.slug, "barreau", e.target.value)} className={input} />
                </div>
                <div>
                  <label className="label-accent block mb-3">Langues</label>
                  <input value={m.languages} onChange={(e) => update(m.slug, "languages", e.target.value)} className={input} />
                </div>
                <div>
                  <label className="label-accent block mb-3">Email</label>
                  <input value={m.email || ""} onChange={(e) => update(m.slug, "email", e.target.value)} className={input} data-testid={`admin-member-email-${m.slug}`} />
                </div>
                <div>
                  <label className="label-accent block mb-3">Téléphone</label>
                  <input value={m.phone || ""} onChange={(e) => update(m.slug, "phone", e.target.value)} className={input} data-testid={`admin-member-phone-${m.slug}`} />
                </div>
              </div>
              <div className="mt-6">
                <label className="label-accent block mb-3">Biographie (FR)</label>
                <textarea rows={4} value={m.bio} onChange={(e) => update(m.slug, "bio", e.target.value)} className={input + " leading-relaxed"} data-testid={`admin-member-bio-${m.slug}`} />
              </div>
              <div className="mt-6">
                <label className="label-accent block mb-3">Biographie (EN)</label>
                <textarea rows={4} value={m.bio_en || ""} onChange={(e) => update(m.slug, "bio_en", e.target.value)} className={input + " leading-relaxed"} data-testid={`admin-member-bio-en-${m.slug}`} />
              </div>
              <div className="mt-6">
                <label className="label-accent block mb-3">Biographie (NL)</label>
                <textarea rows={4} value={m.bio_nl || ""} onChange={(e) => update(m.slug, "bio_nl", e.target.value)} className={input + " leading-relaxed"} data-testid={`admin-member-bio-nl-${m.slug}`} />
              </div>
              <div className="mt-6 flex items-center gap-4">
                <button
                  onClick={() => save(m)}
                  data-testid={`admin-member-save-${m.slug}`}
                  className="inline-flex items-center gap-2 bg-gold text-ink text-xs uppercase tracking-[0.25em] font-medium px-6 py-3 hover:bg-gold-light transition-colors"
                >
                  <Save className="w-4 h-4" /> Enregistrer
                </button>
                {savedSlug === m.slug && (
                  <span className="text-gold text-sm" data-testid={`admin-member-saved-${m.slug}`}>Enregistré.</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
